import { faFilter } from "@fortawesome/pro-regular-svg-icons";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { AnimatePresence } from "framer-motion";
import { useCallback, useState } from "react";
import { DateRange, SelectRangeEventHandler } from "react-day-picker";

import { Icon } from "src/base-components/Icon";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { DropdownItem } from "src/design-system/Dropdown";
import { EventsFilterDropdown } from "src/eventsCatalogue/EventsFilterDropdown";
import { EventFilterFormData } from "src/eventsCatalogue/filters/types";
import { UrlFilterParams } from "src/eventsCatalogue/hooks/useEventFiltersUrl";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";

export type FilterMode = "all" | "pinned" | "custom_filters";

type RelatedEventsFilterDropdownProps = {
  displayName: string;
  hasPinnedEvents: boolean;
  eventType: string;
  onCustomFiltersOpenChange: (isOpen: boolean) => void;
  onApplyFilters: (
    mode: FilterMode,
    formData?: EventFilterFormData,
    dateRange?: DateRange,
  ) => void;
  onClearFilters: () => void;
  dateRangeValue: DateRange | undefined;
  onDateRangeChange: SelectRangeEventHandler;
  hasFilterCondition?: boolean;
  urlFilterParams?: UrlFilterParams | null;
};

export const RelatedEventsFilterDropdown: React.FC<
  RelatedEventsFilterDropdownProps
> = ({
  displayName,
  hasPinnedEvents,
  eventType,
  onApplyFilters,
  onClearFilters,
  dateRangeValue,
  onDateRangeChange,
  hasFilterCondition,
  onCustomFiltersOpenChange,
  urlFilterParams,
}) => {
  const [filteringMode, setFilterMode] = useState<FilterMode>("all");
  const [openMode, _setOpenMode] = useState<"main" | "custom_filters" | false>(
    false,
  );

  const setOpenMode = useCallback(
    (
      newOpenMode: "main" | "custom_filters" | false,
      newFilterMode?: FilterMode,
    ) => {
      if (newFilterMode) {
        setFilterMode(newFilterMode);
      }

      _setOpenMode(newOpenMode);
      onCustomFiltersOpenChange?.(
        (filteringMode === "custom_filters" && newOpenMode !== false) ||
          newOpenMode === "custom_filters",
      );
    },
    [onCustomFiltersOpenChange, filteringMode],
  );

  const filterOptions = [
    { key: "all", value: `All ${displayName}`, disabled: false },
    {
      key: "pinned",
      value: `Pinned ${displayName}`,
      disabled: !hasPinnedEvents,
    },
    isFeatureFlagEnabled(FEATURE_FLAGS.filterableEvents)
      ? { key: "custom_filters", value: "Custom filters", disabled: false }
      : null,
  ].filter((option): option is NonNullable<typeof option> => option !== null);

  const handleOpenChange = () => {
    if (typeof openMode === "string") {
      return setOpenMode(false);
    }

    if (filteringMode === "custom_filters") {
      return setOpenMode("custom_filters");
    }

    return setOpenMode("main");
  };

  const handleFilterModeChange = useCallback(
    (mode: FilterMode) => {
      if (mode === "custom_filters") {
        setOpenMode("custom_filters", mode);
      } else {
        onApplyFilters(mode);
        setOpenMode(false, mode);
      }
    },
    [setOpenMode, onApplyFilters],
  );

  const handleApplyFilters = useCallback(
    (formData: EventFilterFormData, dateRange: DateRange | undefined) => {
      onApplyFilters("custom_filters", formData, dateRange);
    },
    [onApplyFilters],
  );

  return (
    <>
      <Listbox
        as="div"
        className="relative"
        value={filteringMode}
        onChange={handleFilterModeChange}
      >
        <ListboxButton
          className="flex items-center gap-x-1 rounded-lg border-none px-2 py-1.5 focus:outline-none focus:ring-0"
          data-loc="events-filter-dropdown-button"
          onClick={handleOpenChange}
        >
          <Icon color="text-gray-500" icon={faFilter} size="xs" />
        </ListboxButton>
        <AnimatePresence>
          {openMode === "main" && (
            <ListboxOptions
              anchor={{ to: "bottom end", gap: 8 }}
              as={MotionDropdownPanel}
              data-loc="events-filter-dropdown"
              static
            >
              {filterOptions.map((option) => (
                <ListboxOption
                  key={option.key}
                  as={DropdownItem}
                  disabled={option.disabled}
                  value={option.key}
                >
                  <div className="flex items-center justify-between gap-x-1">
                    {option.value}
                  </div>
                </ListboxOption>
              ))}
            </ListboxOptions>
          )}
        </AnimatePresence>
      </Listbox>
      {isFeatureFlagEnabled(FEATURE_FLAGS.filterableEvents) && (
        <EventsFilterDropdown
          // We do not need button as we already rendered one above
          buttonRenderer={() => <div className="h-px w-full -translate-y-1" />}
          dateRangeValue={dateRangeValue}
          eventType={eventType}
          hasFilterCondition={hasFilterCondition}
          isOpen={openMode === "custom_filters"}
          urlFilterParams={urlFilterParams}
          onApplyFilters={handleApplyFilters}
          onClearFilters={onClearFilters}
          onClickBack={() => setOpenMode("main")}
          onDateRangeChange={onDateRangeChange}
          onOpenChange={(isOpen) =>
            setOpenMode(isOpen ? "custom_filters" : false)
          }
        />
      )}
    </>
  );
};
